import type { WidgetConfiguration } from '@sg-widgets/shared-core';
import { Component } from 'react';
import { BUS_USER_INFO, type ISgConnectUserInfo } from '../../common/auth/bus-topics';
import type { IWidgetConfigurationContext } from '../../common/configuration';
import { SgwtTranslationProvider } from '../../common/i18n';
import { SgwtWidgetName, startWidgetMonitoring } from '../../common/monitoring';
import { isProductionEnvironment } from '../../common/sgwt-widgets-utils';
import {
  type AlternateTokenGetterFunction,
  type AnyChatbotContext,
  isValidAdapter,
  type SgwtChatbotAdapter,
  type SgwtChatbotContext,
} from '../../common/types/sgwt-chatbot.adapter';
import { widgetize, widgetPropsBoundEvent } from '../../common/widgetize';
import { getWidgetBus } from '../../common/widgets-bus';
import { DxAgentFactoryAdapter } from './adapters/dx-agent-factory/dx-agent-factory.adapter';
import { DxAssistantAdapter } from './adapters/dx-assistant/dx-assistant.adapter';
import { WebChat } from './components/WebChat';
import { translationMessages } from './locales';
import { BotConversationProvider } from './shared/BotConversationContext';
import type { ChatbotAdapter, SgwtChatbotMessage } from './shared/chatbot.types';
import './sgwt-chatbot.scss';

// TODO Add monitoring...

export interface SgwtChatbotPublicFields {
  setChatbotAdapter: (adapter: SgwtChatbotAdapter) => void;
  setAlternateTokenGetterFunction: (handler: AlternateTokenGetterFunction) => void;
}

interface SgwtChatbotDispatchProps {
  onClose: (data?: Record<string, unknown>) => void;
  onGoBack: (data?: Record<string, unknown>) => void;
  onResize: (data?: Record<string, unknown>) => void;
  onReady: (data?: Record<string, unknown>) => void;
}

interface SgwtChatbotProps extends SgwtChatbotDispatchProps {
  conversationId?: string;
  initialMessage?: string;
  suggestions?: string; // JSON object
  chatBeforeContactUs?: boolean;
  embeddedInHelpCenter?: boolean; // This attribute is set to "true" when the chatbot is embedded in the Help-Center widget, it allows the chatbot to adapt its behavior in this specific context (ex: better management of the conversation history). It is not a public attribute, and should not be set by the users of the chatbot widget.
  //
  adapter?: string; // 'custom' or 'dx-assistant'
  chatbotContext?: string; // JSON object
}

interface SgwtChatbotState {
  botAdapter: SgwtChatbotAdapter | undefined;
  chatbotContext: SgwtChatbotContext;
}

function isSgwtChatbotEvent(props: Readonly<SgwtChatbotDispatchProps>, value: string): value is keyof SgwtChatbotDispatchProps {
  return Object.keys(props).includes(value) && typeof props[value as keyof SgwtChatbotDispatchProps] === 'function';
}

// Convert something to an object.
// It can be a JSON stringified object, but sometimes with single quotes or HTML entities, or it can be an object already.
const convertToObject = (str: string | object | null | undefined, defaultValue?: object) => {
  if (!str) {
    return defaultValue;
  }
  if (typeof str === 'object') {
    return str;
  }
  if (typeof str === 'string') {
    try {
      return JSON.parse(str);
    } catch {
      try {
        // in some cases the JSON can be sent with single quotes or HTML entitied.
        // (e.g. `{'mail': 'sgmarkets@sgcib.com'}` or `{&quot;mail&quot;: &quot;sgmarkets@sgcib.com&quot;}`).
        // This code transforms the string.
        return JSON.parse(str.replace(/&quot;/g, '"').replace(/'/g, '"'));
      } catch {
        console.warn('<sgwt-chatbot> Unable to parse JSON object', str);
      }
    }
  }
  return defaultValue;
};

const getAsArray = (value: string | string[] | null | undefined): string[] | undefined => {
  if (!value) {
    return undefined;
  }
  return Array.isArray(value) ? value : value.split(',').map((s) => s.trim());
};

const createChatbotContext = (
  widgetConfiguration: WidgetConfiguration | undefined,
  adapter: ChatbotAdapter,
  specificContext: Record<string, any>,
): AnyChatbotContext => {
  const user = getWidgetBus().dangerouslyGetCurrentValue<ISgConnectUserInfo | undefined>(BUS_USER_INFO);
  const environment = isProductionEnvironment(widgetConfiguration) ? 'production' : 'homologation';

  if (adapter === 'dx-assistant') {
    return {
      ...specificContext,
      knowledgeSpace: specificContext.knowledgeSpace ?? 'dx',
      scopes: getAsArray(specificContext.scopes),
      tags: getAsArray(specificContext.tags),
      user,
      environment,
      adapter: 'dx-assistant',
    };
  }

  if (adapter === 'dx-agent-factory') {
    const agentId = specificContext.agentId;
    if (!agentId) {
      console.error('<sgwt-chatbot> For "dx-agent-factory" adapter, "agentId" must be provided in the context.');
    }
    return {
      ...specificContext,
      user,
      environment,
      adapter: 'dx-agent-factory',
      agentId,
    };
  }

  return {
    ...specificContext,
    user,
    environment,
    adapter: 'custom',
  };
};

export class SgwtChatbot extends Component<SgwtChatbotProps, SgwtChatbotState> {
  public static readonly is = 'sgwt-chatbot';

  private propsBoundEvent = widgetPropsBoundEvent(SgwtChatbot.is);
  private onWidgetReady = (): void => {
    this.props.onReady({});
  };

  constructor(props: SgwtChatbotProps, context: IWidgetConfigurationContext) {
    super(props);
    const { adapter } = props;

    const specificContext = convertToObject(props.chatbotContext, {});

    const chatbotContext = createChatbotContext(
      context?.widgetConfiguration,
      (['dx-assistant', 'dx-agent-factory', 'custom'].find((a) => a === adapter) as ChatbotAdapter) ?? 'custom',
      specificContext,
    );

    let botAdapter: SgwtChatbotAdapter | undefined;
    if (adapter === 'dx-assistant') {
      botAdapter = new DxAssistantAdapter(chatbotContext);
    } else if (adapter === 'dx-agent-factory') {
      botAdapter = new DxAgentFactoryAdapter(chatbotContext);
    } else if (adapter !== 'custom') {
      console.warn(
        '<sgwt-chatbot> No valid adapter specified, please set the "adapter" attribute to "custom", "dx-assistant" or "dx-agent-factory".',
      );
    }
    console.debug(`[sgwt-chatbot] Chatbot configured with adapter "${adapter ?? 'none'}"`, chatbotContext);

    this.state = {
      botAdapter,
      chatbotContext,
    };
  }

  public componentDidMount() {
    document.addEventListener(this.propsBoundEvent, this.onWidgetReady);
    startWidgetMonitoring(SgwtWidgetName.SGWT_CHATBOT, null, {
      adapter: this.props.adapter,
      conversationId: this.props.conversationId,
      initialMessage: this.props.initialMessage,
      chatBeforeContactUs: this.props.chatBeforeContactUs,
      embeddedInHelpCenter: this.props.embeddedInHelpCenter,
    });
  }

  componentWillUnmount = (): void => {
    document.removeEventListener(this.propsBoundEvent, this.onWidgetReady);
  };

  private emitEvent = (name: string, detail?: Record<string, unknown>): void => {
    const event = EVENTS_META.find((event) => event.name.endsWith(name));
    if (!event) {
      return;
    }
    if (isSgwtChatbotEvent(this.props, event.functionName)) {
      this.props[event.functionName](detail);
    } else {
    }
  };

  public handleGoBack = () => this.emitEvent('go-back');
  public handleClose = () => this.emitEvent('close');
  public handleResize = (expanded: boolean) => this.emitEvent('resize', { expanded });

  public onMessage = (message: SgwtChatbotMessage) => {
    this.emitEvent('message', { message });
  };

  /**
   * Give to the <sgwt-chatbot> widget the implementation of a custom adapter.
   */
  public setChatbotAdapter = (adapter: SgwtChatbotAdapter) => {
    if (isValidAdapter(adapter)) {
      this.setState({ ...this.state, botAdapter: adapter });
    } else {
      console.error('<sgwt-chatbot> The provided adapter is not valid, please ensure it implements the required interface.');
    }
  };

  /**
   * Some adapters, like the "dx-agent-factory" adapter, may require an alternate token (i.e. a token provided by the other SG-Connect authority server) to call the API.
   * In such situation, the widget may has access to a function to retrieve this alternate token, that is set via the "setAlternateTokenGetterFunction" method.
   * This function should return a promise resolving to the alternate token.
   */
  public setAlternateTokenGetterFunction = (handler: AlternateTokenGetterFunction) => {
    if (typeof handler === 'function') {
      if (this.props.adapter === 'dx-agent-factory' && this.state.botAdapter instanceof DxAgentFactoryAdapter) {
        console.info('<sgwt-chatbot> Called "setAlternateTokenGetterFunction".');
        this.state.botAdapter.setAlternateTokenGetterFunction(handler);
      } else {
        console.error('<sgwt-chatbot> The "setAlternateTokenGetterFunction" method is only available for the "dx-agent-factory" adapter.');
      }
    } else {
      console.error('<sgwt-chatbot> The provided handler is not a function, please provide a valid function.');
    }
  };

  render() {
    return (
      <SgwtTranslationProvider messages={translationMessages}>
        <BotConversationProvider
          conversationId={this.props.conversationId}
          initialMessage={this.props.initialMessage === 'null' ? undefined : this.props.initialMessage}
          chatBeforeContactUs={this.props.chatBeforeContactUs}
          embeddedInHelpCenter={this.props.embeddedInHelpCenter ?? false}
          chatbotContext={this.state.chatbotContext}
          botAdapter={this.state.botAdapter}
          onNewMessage={this.onMessage}
        >
          <WebChat
            onGoBack={this.handleGoBack}
            onClose={this.handleClose}
            onResize={this.handleResize}
            suggestions={convertToObject(this.props.suggestions)}
          />
        </BotConversationProvider>
      </SgwtTranslationProvider>
    );
  }
}

const EVENTS_META = [
  {
    name: `${SgwtChatbot.is}--ready`,
    functionName: 'onReady',
  },
  {
    name: `${SgwtChatbot.is}--close`,
    functionName: 'onClose',
  },
  {
    name: `${SgwtChatbot.is}--go-back`,
    functionName: 'onGoBack',
  },
  {
    name: `${SgwtChatbot.is}--resize`,
    functionName: 'onResize',
  },
  {
    name: `${SgwtChatbot.is}--message`,
    functionName: 'onMessage',
  },
];

widgetize(SgwtChatbot, SgwtChatbot.is, {
  attributes: [
    { name: 'embedded-in-help-center', type: 'boolean' },
    { name: 'chat-before-contact-us', type: 'boolean' },
    { name: 'conversation-id', type: 'string' },
    { name: 'initial-message', type: 'string' },
    { name: 'suggestions', type: 'string' },
    { name: 'adapter', type: 'string' },
    { name: 'chatbot-context', type: 'object' },
  ],
  events: EVENTS_META,
  deferredFunctions: ['setChatbotAdapter'],
});
